import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Check, AlertCircle, Clock, LogOut, Download, Pen, Stamp, Palette, Grid3X3, ChevronDown, ChevronUp, Plus, Trash2, Send, Info, Eye, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { SHADE_CARDS } from "@/components/ShadeCardSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shade Card Data - kept minimal for input placeholder references
const shadeCards = {
  plbShades: { shades: [{ code: "8017" }] },
  metalShades: { shades: [{ code: "MT-001" }] },
  fabricShades: { shades: [{ code: "PKC-1018" }] },
  backPaintedGlass: { shades: [{ code: "8499" }] },
  acousticShades: { shades: [{ code: "AC-001" }] },
  laminateShades: { shades: [{ code: "LM-001" }] },
};

// PDF Catalogue Viewer for Client Portal
const CatalogueCard = ({ card, onViewPdf }) => {
  return (
    <div className="bg-white border border-[#E2E8F0] mb-3 hover:border-[#F97316] transition-colors">
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: card.color }}>
            <card.icon className="w-4 h-4 text-white" />
          </div>
          <span className="font-medium text-sm text-[#0F172A] truncate">{card.shortName}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={card.pdfUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 px-3 text-xs font-medium border border-[#E2E8F0] rounded flex items-center gap-1.5 hover:bg-[#F8FAFC] transition-colors text-[#64748B]"
            data-testid={`portal-download-${card.id}`}
          >
            <Download className="w-3 h-3" />
            Download
          </a>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewPdf(card)}
            className="h-8 text-xs"
            style={{ color: card.color }}
            data-testid={`portal-view-${card.id}`}
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
        </div>
      </div>
    </div>
  );
};

// PDF Modal for Client Portal
const PortalPdfModal = ({ card, onClose }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(card.pdfUrl)}&embedded=true`;

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative w-[95vw] max-w-5xl h-[85vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        data-testid={`portal-modal-${card.id}`}
      >
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b bg-[#0F172A] text-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <card.icon className="w-5 h-5 shrink-0" style={{ color: card.color }} />
            <h2 className="text-sm md:text-base font-bold truncate">{card.name}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <a
              href={card.pdfUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold rounded text-white transition-all hover:brightness-110"
              style={{ backgroundColor: card.color }}
              data-testid={`portal-pdf-download-${card.id}`}
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white">
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>
        </div>
        <div className="flex-1 relative bg-[#f1f1f1]">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f8f8f8] z-10">
              <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: card.color }} />
              <p className="text-sm text-[#64748B]">Loading catalogue...</p>
            </div>
          )}
          <iframe src={googleViewerUrl} title={card.name} className="w-full h-full border-0" loading="lazy" onLoad={() => setLoading(false)} />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ProductApprovalForm Component - Moved outside to prevent re-renders
const ProductApprovalForm = ({ categoryKey, config, approvals, onUpdate }) => {
  return (
    <div className="bg-white border border-[#E2E8F0] p-4 mb-4">
      <h4 className="font-bold text-[#0F172A] mb-4">{config.name}</h4>
      <div className="space-y-3">
        {config.fields.map((field) => (
          <div key={field.key} className="flex items-center gap-3">
            <label className="text-sm text-[#64748B] w-56 shrink-0">{field.label}:</label>
            <Input
              value={approvals?.[field.key] || ""}
              onChange={(e) => onUpdate(categoryKey, field.key, e.target.value)}
              placeholder={`Enter ${shadeCards[field.shadeType]?.shades[0]?.code || 'code'}...`}
              className="flex-1 h-9 text-sm font-mono"
              data-testid={`input-${categoryKey}-${field.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// MultipleProductForm Component - Moved outside to prevent re-renders
const MultipleProductForm = ({ categoryKey, config, multipleItems, addMultipleItem, removeMultipleItem, updateMultipleItemSubheadline, updateMultipleItemShade }) => {
  const items = multipleItems[categoryKey] || [];
  
  return (
    <div className="bg-white border border-[#E2E8F0] p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-[#0F172A]">{config.name}</h4>
        <Button
          variant="outline"
          size="sm"
          onClick={() => addMultipleItem(categoryKey)}
          className="h-8"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add More
        </Button>
      </div>
      
      {items.map((item, index) => (
        <div key={item.id} className="border border-[#E2E8F0] p-3 mb-3 bg-[#F8FAFC]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#64748B]">#{index + 1}</span>
            {items.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeMultipleItem(categoryKey, item.id)}
                className="h-7 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          
          {config.hasSubheadline && (
            <div className="mb-3">
              <Input
                value={item.subheadline}
                onChange={(e) => updateMultipleItemSubheadline(categoryKey, item.id, e.target.value)}
                placeholder="Enter sub-headline (e.g., MD Cabin, Director Cabin...)"
                className="h-9 text-sm"
                data-testid={`input-${categoryKey}-${item.id}-subheadline`}
              />
            </div>
          )}
          
          <div className="space-y-2">
            {config.fields.map((field) => (
              <div key={field.key} className="flex items-center gap-3">
                <label className="text-sm text-[#64748B] w-48 shrink-0">{field.label}:</label>
                <Input
                  value={item.shades?.[field.key] || ""}
                  onChange={(e) => updateMultipleItemShade(categoryKey, item.id, field.key, e.target.value)}
                  placeholder={`Enter ${shadeCards[field.shadeType]?.shades[0]?.code || 'code'}...`}
                  className="flex-1 h-8 text-sm font-mono"
                  data-testid={`input-${categoryKey}-${item.id}-${field.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Product categories with their shade requirements
const productCategories = {
  workstations: {
    name: "WORKSTATIONS",
    fields: [
      { key: "tableTopPLB", label: "Table Top PLB shade", shadeType: "plbShades" },
      { key: "metalLeg", label: "Metal Leg shade", shadeType: "metalShades" },
      { key: "pinupFabric", label: "Pinup Board Fabric shade", shadeType: "fabricShades" },
      { key: "backpaintedScreen", label: "Backpainted Privacy Screen", shadeType: "backPaintedGlass" },
    ],
    allowMultiple: false,
  },
  cabinTables: {
    name: "CABIN TABLE",
    fields: [
      { key: "tableTopPLB", label: "PLB shade of Table Top", shadeType: "plbShades" },
      { key: "tableBasePLB", label: "PLB Shade of Table Base", shadeType: "plbShades" },
      { key: "metalLeg", label: "Metal Leg shade", shadeType: "metalShades" },
    ],
    allowMultiple: true,
    hasSubheadline: true,
  },
  cabinSideboards: {
    name: "CABIN SIDEBOARD",
    fields: [
      { key: "sideboardTopPLB", label: "PLB shade of Side Board Top", shadeType: "plbShades" },
      { key: "sideboardShuttersPLB", label: "PLB shade of Side Board shutters", shadeType: "plbShades" },
    ],
    allowMultiple: true,
    hasSubheadline: true,
  },
  receptionDesk: {
    name: "RECEPTION DESK",
    fields: [
      { key: "plbShade", label: "PLB shade", shadeType: "plbShades" },
    ],
    allowMultiple: false,
  },
  wallCabinet: {
    name: "WALL MOUNTED FILE CABINET",
    fields: [
      { key: "carcassPLB", label: "PLB shade of Wall Cabinet Carcass", shadeType: "plbShades" },
      { key: "shuttersPLB", label: "PLB shade of Wall Cabinet shutters", shadeType: "plbShades" },
    ],
    allowMultiple: false,
  },
  floorCabinet: {
    name: "FLOOR MOUNTED FILE CABINETS",
    fields: [
      { key: "carcassPLB", label: "PLB shade of Floor Cabinet Carcass", shadeType: "plbShades" },
      { key: "shuttersPLB", label: "PLB shade of Floor Cabinet shutters", shadeType: "plbShades" },
    ],
    allowMultiple: false,
  },
  conferenceTable: {
    name: "CONFERENCE TABLE",
    fields: [
      { key: "tableTopPLB", label: "PLB Shade of Table Top", shadeType: "plbShades" },
      { key: "tableBasePLB", label: "PLB Shade of Table Base", shadeType: "plbShades" },
      { key: "powdercoatingBase", label: "Powder-coating Shade of Table Base", shadeType: "metalShades" },
    ],
    allowMultiple: false,
  },
  meetingTable: {
    name: "MEETING TABLE",
    fields: [
      { key: "tableTopPLB", label: "PLB Shade of Table Top", shadeType: "plbShades" },
      { key: "tableBasePLB", label: "PLB Shade of Table Base", shadeType: "plbShades" },
      { key: "powdercoatingBase", label: "Powder-coating Shade of Table Base", shadeType: "metalShades" },
    ],
    allowMultiple: false,
  },
  generalTables: {
    name: "GENERAL TABLES",
    fields: [
      { key: "tableTopPLB", label: "PLB shade of Table Top", shadeType: "plbShades" },
      { key: "tableBasePLB", label: "PLB Shade of Table Base", shadeType: "plbShades" },
      { key: "metalLeg", label: "Metal Leg shade", shadeType: "metalShades" },
    ],
    allowMultiple: false,
  },
};

export default function ClientDashboard() {
  const [user, setUser] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("drawings");
  const [portalPdfCard, setPortalPdfCard] = useState(null);
  const [productApprovals, setProductApprovals] = useState({});
  const [multipleItems, setMultipleItems] = useState({
    cabinTables: [{ id: 1, subheadline: "", shades: {} }],
    cabinSideboards: [{ id: 1, subheadline: "", shades: {} }],
  });
  const [existingApprovals, setExistingApprovals] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token || !userData) {
      navigate("/portal/login");
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchDrawings(token);
    fetchProductApprovals(token);
  }, [navigate]);

  const fetchDrawings = async (token) => {
    try {
      const response = await axios.get(`${API}/drawings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrawings(response.data);
    } catch (error) {
      toast.error("Failed to fetch drawings");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductApprovals = async (token) => {
    try {
      const response = await axios.get(`${API}/product-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.approvals) {
        setExistingApprovals(response.data);
        // Pre-fill form with existing approvals
        setProductApprovals(response.data.approvals || {});
        if (response.data.multipleItems) {
          setMultipleItems(response.data.multipleItems);
        }
      }
    } catch (error) {
      console.log("No existing product approvals");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/portal/login");
  };

  const viewDrawing = async (drawing) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/drawings/${drawing.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDrawing(response.data);
      setApprovalRemarks("");
    } catch (error) {
      toast.error("Failed to load drawing details");
    }
  };

  const downloadDrawing = async () => {
    if (!selectedDrawing?.file_data) {
      toast.error("Drawing file not available");
      return;
    }
    
    try {
      const byteCharacters = atob(selectedDrawing.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: selectedDrawing.file_type || 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedDrawing.file_name || 'drawing.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started!");
    } catch (error) {
      toast.error("Failed to download drawing");
    }
  };

  const handleApproval = async (status) => {
    if (!selectedDrawing) return;
    
    const token = localStorage.getItem("token");
    setIsSubmitting(true);
    
    let signatureData = null;
    if (canvasRef.current) {
      signatureData = canvasRef.current.toDataURL();
    }
    
    try {
      await axios.put(
        `${API}/drawings/${selectedDrawing.id}/approve`,
        {
          status,
          remarks: approvalRemarks,
          signature_data: signatureData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(status === "approved" ? "Drawing approved!" : "Revision requested");
      setSelectedDrawing(null);
      setShowConfirmation(status === "approved");
      fetchDrawings(token);
    } catch (error) {
      toast.error("Failed to submit approval");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProductShade = (category, fieldKey, value) => {
    setProductApprovals(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [fieldKey]: value
      }
    }));
  };

  const updateMultipleItemShade = (category, itemId, fieldKey, value) => {
    setMultipleItems(prev => ({
      ...prev,
      [category]: prev[category].map(item =>
        item.id === itemId
          ? { ...item, shades: { ...item.shades, [fieldKey]: value } }
          : item
      )
    }));
  };

  const updateMultipleItemSubheadline = (category, itemId, subheadline) => {
    setMultipleItems(prev => ({
      ...prev,
      [category]: prev[category].map(item =>
        item.id === itemId ? { ...item, subheadline } : item
      )
    }));
  };

  const addMultipleItem = (category) => {
    const newId = Math.max(...multipleItems[category].map(i => i.id), 0) + 1;
    setMultipleItems(prev => ({
      ...prev,
      [category]: [...prev[category], { id: newId, subheadline: "", shades: {} }]
    }));
  };

  const removeMultipleItem = (category, itemId) => {
    if (multipleItems[category].length > 1) {
      setMultipleItems(prev => ({
        ...prev,
        [category]: prev[category].filter(item => item.id !== itemId)
      }));
    }
  };

  const submitAllApprovals = async () => {
    const token = localStorage.getItem("token");
    setIsSubmitting(true);

    try {
      await axios.post(
        `${API}/product-approvals`,
        {
          approvals: productApprovals,
          multipleItems: multipleItems
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("All shade approvals submitted successfully! Ezeefit staff has been notified.");
      fetchProductApprovals(token);
    } catch (error) {
      toast.error("Failed to submit approvals");
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadShadeCard = (cardName) => {
    const card = SHADE_CARDS.find(c => c.name === cardName || c.shortName === cardName);
    if (card) {
      window.open(card.pdfUrl, "_blank");
      toast.success(`Opening ${card.shortName} catalogue...`);
    }
  };

  // Canvas drawing for signature
  const startDrawingCanvas = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      revision_required: "bg-red-100 text-red-800"
    };
    const icons = {
      pending: Clock,
      approved: Check,
      revision_required: AlertCircle
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="client-dashboard">
      {/* Header */}
      <div className="bg-[#0F172A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Welcome, {user?.name}</h1>
            <p className="text-[#94A3B8]">{user?.company_name}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-white/30 text-white hover:bg-white hover:text-[#0F172A]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex gap-0">
            <button
              onClick={() => setActiveTab("drawings")}
              className={`px-6 py-4 font-semibold transition-colors border-b-2 ${
                activeTab === "drawings"
                  ? "border-[#F97316] text-[#F97316]"
                  : "border-transparent text-[#64748B] hover:text-[#0F172A]"
              }`}
              data-testid="tab-drawings"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Shop Drawings
            </button>
            <button
              onClick={() => setActiveTab("shades")}
              className={`px-6 py-4 font-semibold transition-colors border-b-2 ${
                activeTab === "shades"
                  ? "border-[#F97316] text-[#F97316]"
                  : "border-transparent text-[#64748B] hover:text-[#0F172A]"
              }`}
              data-testid="tab-shades"
            >
              <Palette className="w-4 h-4 inline mr-2" />
              Material Shades
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {activeTab === "drawings" ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Drawings List */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-[#0F172A] mb-6">Production Drawings</h2>
              
              {drawings.length === 0 ? (
                <div className="bg-white border border-[#E2E8F0] p-12 text-center">
                  <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#64748B]">No drawings available yet</p>
                  <p className="text-sm text-[#94A3B8] mt-2">
                    You'll be notified when new drawings are uploaded for approval
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {drawings.map((drawing) => (
                    <motion.div
                      key={drawing.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-[#E2E8F0] p-6 hover:border-[#F97316] transition-colors cursor-pointer"
                      onClick={() => viewDrawing(drawing)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#F8FAFC] flex items-center justify-center">
                            <FileText className="w-6 h-6 text-[#F97316]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#0F172A]">{drawing.drawing_name}</h3>
                            <p className="text-sm text-[#64748B]">{drawing.project_name}</p>
                            <p className="text-xs text-[#94A3B8] mt-1">
                              {new Date(drawing.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(drawing.status)}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Sidebar */}
            <div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-6">Summary</h2>
              <div className="space-y-4">
                <div className="bg-white border border-[#E2E8F0] p-6">
                  <div className="text-3xl font-bold text-[#0F172A]">{drawings.length}</div>
                  <div className="text-sm text-[#64748B]">Total Drawings</div>
                </div>
                <div className="bg-white border border-[#E2E8F0] p-6">
                  <div className="text-3xl font-bold text-yellow-600">
                    {drawings.filter(d => d.status === "pending").length}
                  </div>
                  <div className="text-sm text-[#64748B]">Pending Approval</div>
                </div>
                <div className="bg-white border border-[#E2E8F0] p-6">
                  <div className="text-3xl font-bold text-green-600">
                    {drawings.filter(d => d.status === "approved").length}
                  </div>
                  <div className="text-sm text-[#64748B]">Approved</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Material Shades Tab */
          <div>
            {/* Existing Approval Notice */}
            {existingApprovals?.status === "submitted" && (
              <div className="bg-green-50 border border-green-200 p-4 mb-6 flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">Shade Approvals Submitted</p>
                  <p className="text-sm text-green-700">
                    Your shade selections were submitted on {new Date(existingApprovals.submitted_at).toLocaleDateString()}. 
                    Ezeefit staff will incorporate these into your production drawings.
                  </p>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Shade Reference Catalogues */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">Shade Catalogues</h3>
                <p className="text-sm text-[#64748B] mb-4">
                  View or download shade catalogues. Use shade codes when filling the form.
                </p>
                
                {SHADE_CARDS.map((card) => (
                  <CatalogueCard key={card.id} card={card} onViewPdf={setPortalPdfCard} />
                ))}
              </div>

              {/* Product Approval Forms */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-[#0F172A]">MODULAR OFFICE FURNITURE</h2>
                    <p className="text-sm text-[#64748B]">
                      Enter approved shade codes for each product component
                    </p>
                  </div>
                </div>

                {/* Product Forms */}
                <ProductApprovalForm
                  categoryKey="workstations"
                  config={productCategories.workstations}
                  approvals={productApprovals.workstations}
                  onUpdate={updateProductShade}
                />

                <MultipleProductForm
                  categoryKey="cabinTables"
                  config={productCategories.cabinTables}
                  multipleItems={multipleItems}
                  addMultipleItem={addMultipleItem}
                  removeMultipleItem={removeMultipleItem}
                  updateMultipleItemSubheadline={updateMultipleItemSubheadline}
                  updateMultipleItemShade={updateMultipleItemShade}
                />

                <MultipleProductForm
                  categoryKey="cabinSideboards"
                  config={productCategories.cabinSideboards}
                  multipleItems={multipleItems}
                  addMultipleItem={addMultipleItem}
                  removeMultipleItem={removeMultipleItem}
                  updateMultipleItemSubheadline={updateMultipleItemSubheadline}
                  updateMultipleItemShade={updateMultipleItemShade}
                />

                <ProductApprovalForm
                  categoryKey="receptionDesk"
                  config={productCategories.receptionDesk}
                  approvals={productApprovals.receptionDesk}
                  onUpdate={updateProductShade}
                />

                <ProductApprovalForm
                  categoryKey="wallCabinet"
                  config={productCategories.wallCabinet}
                  approvals={productApprovals.wallCabinet}
                  onUpdate={updateProductShade}
                />

                <ProductApprovalForm
                  categoryKey="floorCabinet"
                  config={productCategories.floorCabinet}
                  approvals={productApprovals.floorCabinet}
                  onUpdate={updateProductShade}
                />

                <ProductApprovalForm
                  categoryKey="conferenceTable"
                  config={productCategories.conferenceTable}
                  approvals={productApprovals.conferenceTable}
                  onUpdate={updateProductShade}
                />

                <ProductApprovalForm
                  categoryKey="meetingTable"
                  config={productCategories.meetingTable}
                  approvals={productApprovals.meetingTable}
                  onUpdate={updateProductShade}
                />

                <ProductApprovalForm
                  categoryKey="generalTables"
                  config={productCategories.generalTables}
                  approvals={productApprovals.generalTables}
                  onUpdate={updateProductShade}
                />

                {/* Submit Button */}
                <div className="mt-8 bg-[#0F172A] p-6">
                  <Button
                    onClick={submitAllApprovals}
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[#F97316] hover:bg-orange-600 text-white font-semibold text-lg"
                    data-testid="submit-all-approvals"
                  >
                    {isSubmitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit All Shade Approvals to Ezeefit
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-[#94A3B8] mt-3 text-center">
                    Once submitted, Ezeefit staff will incorporate these shades into your production drawings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Drawing Detail Modal */}
      {selectedDrawing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#0F172A] text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{selectedDrawing.drawing_name}</h2>
                <p className="text-[#94A3B8]">{selectedDrawing.project_name}</p>
              </div>
              <button
                onClick={() => setSelectedDrawing(null)}
                className="p-2 hover:bg-white/10 rounded-full text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* Drawing Preview */}
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-8 mb-6 text-center">
                <FileText className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#64748B] mb-4">Drawing: {selectedDrawing.file_name}</p>
                <Button
                  onClick={downloadDrawing}
                  className="bg-[#F97316] hover:bg-orange-600"
                  data-testid="download-drawing-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Drawing
                </Button>
              </div>

              {/* Description */}
              {selectedDrawing.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#0F172A] mb-2">Description</h3>
                  <p className="text-[#64748B]">{selectedDrawing.description}</p>
                </div>
              )}

              {/* Status */}
              <div className="mb-6">
                <h3 className="font-semibold text-[#0F172A] mb-2">Current Status</h3>
                {getStatusBadge(selectedDrawing.status)}
              </div>

              {/* Approval Section - Only show for pending drawings */}
              {selectedDrawing.status === "pending" && (
                <div className="border-t border-[#E2E8F0] pt-6">
                  <h3 className="font-semibold text-[#0F172A] mb-4">Review & Approve</h3>
                  
                  {/* Remarks */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#334155] mb-2">
                      Remarks / Comments
                    </label>
                    <Textarea
                      value={approvalRemarks}
                      onChange={(e) => setApprovalRemarks(e.target.value)}
                      placeholder="Add any comments or required corrections..."
                      className="min-h-[100px]"
                    />
                  </div>

                  {/* Signature Pad */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#334155] mb-2">
                      <Pen className="w-4 h-4 inline mr-1" />
                      Digital Signature
                    </label>
                    <div className="border border-[#E2E8F0] bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair"
                        onMouseDown={startDrawingCanvas}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSignature}
                      className="mt-2"
                    >
                      Clear Signature
                    </Button>
                  </div>

                  {/* Approval Stamp Preview */}
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 mb-6">
                    <p className="text-sm text-[#64748B] mb-2">Approval will be stamped as:</p>
                    <div className="inline-block border-2 border-green-600 px-4 py-2">
                      <Stamp className="w-4 h-4 inline text-green-600 mr-2" />
                      <span className="font-semibold text-green-600">APPROVED BY</span>
                      <p className="text-sm text-[#0F172A]">{user?.name}</p>
                      <p className="text-xs text-[#64748B]">{user?.company_name}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    <Button
                      onClick={() => handleApproval("approved")}
                      disabled={isSubmitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve Drawing
                    </Button>
                    <Button
                      onClick={() => handleApproval("revision_required")}
                      disabled={isSubmitting}
                      variant="outline"
                      className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Request Revision
                    </Button>
                  </div>
                </div>
              )}

              {/* Show approval info for already processed drawings */}
              {selectedDrawing.status !== "pending" && selectedDrawing.approved_by && (
                <div className="border-t border-[#E2E8F0] pt-6">
                  <h3 className="font-semibold text-[#0F172A] mb-4">Approval Details</h3>
                  <p className="text-sm text-[#64748B]">
                    <strong>Approved by:</strong> {selectedDrawing.approved_by}
                  </p>
                  <p className="text-sm text-[#64748B]">
                    <strong>Date:</strong> {new Date(selectedDrawing.approved_at).toLocaleString()}
                  </p>
                  {selectedDrawing.remarks && (
                    <p className="text-sm text-[#64748B] mt-2">
                      <strong>Remarks:</strong> {selectedDrawing.remarks}
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg p-8"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Drawing Approved</h2>
              
              <div className="bg-[#FEF3C7] border border-[#F59E0B] p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#92400E] mb-1">Important Note</p>
                    <p className="text-sm text-[#92400E]">
                      Actual execution shall be based on these approvals. Deviations may occur 
                      due to made-to-order / customization procurements and shall be accepted 
                      as part of the manufacturing process.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => setShowConfirmation(false)}
                className="bg-[#0F172A] hover:bg-[#1E293B] px-8"
              >
                I Understand
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* PDF Catalogue Modal */}
      <AnimatePresence>
        {portalPdfCard && (
          <PortalPdfModal card={portalPdfCard} onClose={() => setPortalPdfCard(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
